import { AnimatePresence, motion, Transition, Variants } from 'framer-motion'
import React, { useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { usePreviousV2 } from '../../../hooks'

type Props = {
  selectedSlide: number
  // Index is the current index of the Step inside of the Children.map function
  order: number
  children?: React.ReactNode
  className?: string
  /** Stay mounted (hidden + inert) off-screen to keep state */
  keepMounted?: boolean
}
export const sliderTransition = {
  type: 'spring',
  stiffness: 170,
  damping: 26,
} satisfies Transition

export const SliderAnimation = ({
  selectedSlide,
  order,
  children,
  className,
  keepMounted,
}: Props) => {
  const previousStep = usePreviousV2(selectedSlide)

  const goingForward = useRef(true)
  goingForward.current = selectedSlide > (previousStep || -1)

  const firstAnimation =
    previousStep === undefined || previousStep === selectedSlide

  const variants = {
    initial: (goingForward: React.MutableRefObject<boolean>) => ({
      translateX: firstAnimation
        ? '-5%'
        : goingForward.current
        ? '100%'
        : '-100%',
      opacity: 0,
      transition: sliderTransition,
    }),
    animate: {
      translateX: '0%',
      opacity: 1,
      transition: sliderTransition,
    },
    exit: (goingForward: React.MutableRefObject<boolean>) => ({
      translateX: goingForward?.current ? '-100%' : '100%',
      opacity: 0,
      transition: sliderTransition,
    }),
  } satisfies Variants

  const offScreen = keepMounted && selectedSlide !== order
  const content = (
    <motion.div
      // hidden slides skip layout measuring
      layout={offScreen ? false : 'position'}
      transition={sliderTransition}
      className={twMerge(
        'w-full h-full relative overflow-y-auto overflow-x-hidden flex flex-col',
        className,
      )}>
      {children}
    </motion.div>
  )

  if (keepMounted) {
    const selected = selectedSlide === order
    const restingVariant = order > selectedSlide ? 'right' : 'left'
    return (
      <motion.div
        variants={keptMountedVariants}
        transition={sliderTransition}
        initial={false}
        animate={selected ? 'animate' : restingVariant}
        inert={!selected}
        className={twMerge(
          'w-full h-full overflow-hidden',
          selected ? 'relative' : 'absolute inset-0',
          className,
        )}>
        {content}
      </motion.div>
    )
  }

  return (
    <AnimatePresence mode='popLayout'>
      {selectedSlide === order && (
        <motion.div
          variants={variants}
          custom={goingForward}
          initial='initial'
          animate='animate'
          exit='exit'
          className={twMerge(
            'w-full relative h-full overflow-hidden',
            className,
          )}>
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const hiddenAfter = { transitionEnd: { visibility: 'hidden' as const } }

const keptMountedVariants = {
  animate: { translateX: '0%', opacity: 1, visibility: 'visible' },
  left: { translateX: '-100%', opacity: 0, ...hiddenAfter },
  right: { translateX: '100%', opacity: 0, ...hiddenAfter },
} satisfies Variants

export default SliderAnimation
